/**
 * Mouse drag-to-scroll for any horizontal rail.
 *
 * A rail opts in with `data-drag-scroll` on the scrolling element. Touch,
 * trackpad, keyboard and scrollbar scrolling are the browser's own and work
 * without this file — dragging with a mouse is the only thing it adds, so a
 * failed load costs that and nothing else.
 *
 * A rail that also carries `data-drag-snap` settles onto a card when the drag
 * ends, instead of leaving the browser's own scroll-snap to decide. That matters
 * on a `snap-mandatory` rail: releasing mid-card makes the browser snap to the
 * NEAREST snap point, so dragging a card halfway across throws it straight back
 * where it came from and only a drag past the halfway mark ever advances. Here a
 * short drag in a direction means "go that way one card", which is what the
 * gesture looked like it was doing.
 *
 * Marker classes the template styles (rename one without the other and the
 * cursor stops matching the behaviour):
 *   is-draggable  the rail, once dragging is wired up (grab cursor)
 *   is-dragging   the rail, mid-drag (grabbing cursor, no text selection)
 * Snap is suspended with an inline `scroll-snap-type: none` for the release
 * animation rather than a marker class: a class needs a Tailwind rebuild before
 * it means anything, and a settle that silently does nothing until someone runs
 * the CSS build is worse than no settle at all.
 *
 * Touch pointers are left alone: the browser's own inertial scrolling is better
 * than anything reimplemented here, and hijacking it breaks vertical page
 * scrolling that starts inside the rail.
 */
// Wrapped so a page that includes this file twice (the checklist and the
// process rail both do) does not redeclare the class; the second copy is a no-op.
(function () {
    if (window.__dragScrollLoaded) return;
    window.__dragScrollLoaded = true;

    class DragScroll {
        /** Pointer travel (px) before a press counts as a drag rather than a click. */
        static THRESHOLD = 5;

        /**
         * How far a `data-drag-snap` rail must travel before the release counts as
         * "next card" rather than "put it back": a fraction of one card's width, with
         * a pixel floor so a narrow card cannot advance on a twitch.
         */
        static SETTLE_FRACTION = 0.15;
        static SETTLE_MIN_PX = 32;

        /** Length of the release animation, in ms. */
        static SETTLE_MS = 260;

        /**
         * When to give up waiting for animation frames and just be there.
         * `requestAnimationFrame` does not fire in a hidden tab, and a settle that
         * never finishes would leave the rail mid-card with snapping still switched
         * off — so the deadline finishes the job whether or not frames arrived.
         */
        static SETTLE_DEADLINE_MS = 1200;

        constructor(track) {
            this.track = track;
            this.dragging = false;
            this.moved = false;
            this.startX = 0;
            this.startScroll = 0;
            this.startIndex = 0;
            // Where the drag put the rail, as the pointer asked for it. Read back
            // from `scrollLeft` this would already be wrong: releasing re-arms
            // mandatory snap, which moves the rail before the release handler runs.
            this.dragScroll = 0;
            this.frame = 0;
            this.deadline = 0;
            // Opt-in: rails that snap by proximity are happy to rest where they are
            // dropped, and paging them on release would change a gesture that works.
            this.settleOnRelease = track.hasAttribute('data-drag-snap');
        }

        init() {
            if (!this.track) return;
            this.track.classList.add('is-draggable');
            this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event));
            // Capture phase: a drag that ends over a link must not also open it, and
            // the link's own handler would otherwise run first.
            this.track.addEventListener('click', (event) => {
                if (!this.moved) return;
                event.preventDefault();
                event.stopPropagation();
                this.moved = false;
            }, true);
        }

        onPointerDown(event) {
            if (event.pointerType === 'touch' || event.button !== 0) return;
            // Nothing to scroll (a rail that is a plain grid at this width): leave the
            // press alone so a click is never mistaken for a drag and swallowed.
            if (this.track.scrollWidth <= this.track.clientWidth) return;

            this.dragging = true;
            this.moved = false;
            this.startX = event.clientX;
            // A new grab owns the rail — any settle still animating is abandoned
            // where it is rather than fighting the hand on it.
            this.cancelSettle();
            this.startScroll = this.track.scrollLeft;
            this.startIndex = this.nearestIndex();

            const onMove = (moveEvent) => {
                if (!this.dragging) return;
                const delta = moveEvent.clientX - this.startX;
                if (!this.moved && Math.abs(delta) < DragScroll.THRESHOLD) return;
                if (!this.moved) {
                    this.moved = true;
                    this.track.classList.add('is-dragging');
                    // Held from here so the drag survives the pointer leaving the rail.
                    this.track.setPointerCapture?.(moveEvent.pointerId);
                }
                // preventDefault stops the browser starting a text or image selection
                // mid-drag, which would leave the page highlighted blue.
                moveEvent.preventDefault();
                this.dragScroll = this.startScroll - delta;
                this.track.scrollLeft = this.dragScroll;
            };

            const end = () => {
                const dragged = this.moved;
                const settling = dragged && this.settleOnRelease;
                // Before the class comes off, not after: `is-dragging` is what holds
                // snap off, and the gap between losing it and the settle starting is
                // long enough for the browser to snap the rail somewhere else.
                if (settling) this.track.style.scrollSnapType = 'none';
                this.dragging = false;
                this.track.classList.remove('is-dragging');
                this.track.removeEventListener('pointermove', onMove);
                this.track.removeEventListener('pointercancel', end);
                if (settling) this.settle();
            };

            this.track.addEventListener('pointermove', onMove);
            this.track.addEventListener('pointerup', end, { once: true });
            this.track.addEventListener('pointercancel', end);
        }

        /** The rail's cards. Element children only — a stray text node is not a card. */
        cards() {
            return Array.prototype.filter.call(
                this.track.children,
                (node) => node.nodeType === 1,
            );
        }

        /** Index of the card sitting closest to the rail's left edge right now. */
        nearestIndex() {
            const cards = this.cards();
            if (!cards.length) return 0;
            const base = cards[0].offsetLeft;
            const left = this.track.scrollLeft;
            let nearest = 0;
            let best = Infinity;
            cards.forEach((card, index) => {
                const distance = Math.abs(card.offsetLeft - base - left);
                if (distance < best) {
                    best = distance;
                    nearest = index;
                }
            });
            return nearest;
        }

        /**
         * Land on a card after the drag.
         *
         * Direction wins over distance: any drag past the threshold moves at least
         * one card the way it was pulled, and a longer drag moves as many cards as
         * it actually covered. Under the threshold the rail returns to the card it
         * started on, which is the only case where snapping back is what was meant.
         */
        settle() {
            const cards = this.cards();
            if (cards.length < 2) return;

            const base = cards[0].offsetLeft;
            const step = cards[1].offsetLeft - cards[0].offsetLeft;
            if (!(step > 0)) return;

            const delta = this.dragScroll - this.startScroll;
            const threshold = Math.max(DragScroll.SETTLE_MIN_PX, step * DragScroll.SETTLE_FRACTION);

            let index = this.startIndex;
            if (Math.abs(delta) >= threshold) {
                const travelled = delta / step;
                index += delta > 0
                    ? Math.max(1, Math.round(travelled))
                    : Math.min(-1, Math.round(travelled));
            }
            index = Math.max(0, Math.min(cards.length - 1, index));

            const furthest = this.track.scrollWidth - this.track.clientWidth;
            const left = Math.max(0, Math.min(furthest, cards[index].offsetLeft - base));

            this.animateTo(left);
        }

        /**
         * Ease the rail to `left`, then hand snapping back to the browser.
         *
         * Written out rather than handed to `scrollTo({behavior: 'smooth'})`:
         * a smooth programmatic scroll is silently a no-op on a snap container in
         * Chrome, so the rail simply stayed where it was dropped. Assigning
         * `scrollLeft` per frame always moves it, on every engine.
         */
        animateTo(left) {
            const from = this.track.scrollLeft;
            const distance = left - from;

            // Under a pixel, or a reader who asked for less motion: just be there.
            if (Math.abs(distance) < 1 ||
                window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                this.track.scrollLeft = left;
                this.releaseSnap();
                return;
            }

            const started = performance.now();
            const step = (now) => {
                const t = Math.min(1, (now - started) / DragScroll.SETTLE_MS);
                // Ease-out cubic: fastest at the release, settling into the card.
                this.track.scrollLeft = from + distance * (1 - Math.pow(1 - t, 3));
                if (t < 1) {
                    this.frame = window.requestAnimationFrame(step);
                } else {
                    this.finishSettle(left);
                }
            };
            this.frame = window.requestAnimationFrame(step);
            this.deadline = window.setTimeout(
                () => this.finishSettle(left),
                DragScroll.SETTLE_DEADLINE_MS,
            );
        }

        /** Land on `left` and give snapping back, however the settle got here. */
        finishSettle(left) {
            this.clearSettleTimers();
            this.track.scrollLeft = left;
            this.releaseSnap();
        }

        /** Stop a settle in flight, leaving the rail wherever it had reached. */
        cancelSettle() {
            this.clearSettleTimers();
            this.releaseSnap();
        }

        clearSettleTimers() {
            if (this.frame) {
                window.cancelAnimationFrame(this.frame);
                this.frame = 0;
            }
            if (this.deadline) {
                window.clearTimeout(this.deadline);
                this.deadline = 0;
            }
        }

        /** Give scroll-snap back to the stylesheet. */
        releaseSnap() {
            this.track.style.scrollSnapType = '';
        }
    }

    (function () {
        function init() {
            document.querySelectorAll('[data-drag-scroll]').forEach(function (track) {
                if (track.dataset.dragScrollReady) return;
                track.dataset.dragScrollReady = '1';
                new DragScroll(track).init();
            });
        }

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
        } else {
            init();
        }
    })();
})();
